import { makeAutoObservable, runInAction } from 'mobx'
import { MainService } from '~/shared/api/services/main'
import type {
  orderStatistics,
  Statistics,
} from '~/shared/api/services/main/types'
import type { SelectOption } from '~/shared/ui'
import type { Restock } from '~/shared/api/services/products/types'
import type { Stores } from '~/shared/api/services/stores/types'

export class MainModel {
  statisticsLoading = false

  orderStatisticsLoading = false

  restockLoading = false

  statisticsError = false

  orderStatisticsError = false

  restockError = false

  statistics: Statistics | null = null

  orderStatistics: orderStatistics | null = null

  private statisticsRequestId = 0

  private orderStatisticsRequestId = 0

  private restockRequestId = 0

  filterStore: SelectOption | null = null

  statisticsDays = 7

  restockProducts: Restock[] = []

  constructor() {
    makeAutoObservable(this)
  }

  get loading() {
    return (
      this.statisticsLoading ||
      this.orderStatisticsLoading ||
      this.restockLoading
    )
  }

  getStatistics = async (storeId: string, days = this.statisticsDays) => {
    const requestId = this.statisticsRequestId + 1
    this.statisticsRequestId = requestId
    this.statisticsLoading = true
    this.statisticsError = false
    this.statistics = null

    try {
      const { data } = await MainService.getStatistics(storeId, days)
      runInAction(() => {
        if (requestId === this.statisticsRequestId) {
          this.statistics = data
        }
      })
    } catch {
      runInAction(() => {
        if (requestId === this.statisticsRequestId) {
          this.statisticsError = true
        }
      })
    } finally {
      runInAction(() => {
        if (requestId === this.statisticsRequestId) {
          this.statisticsLoading = false
        }
      })
    }
  }

  getOrderStatistics = async (storeId: string, days: number) => {
    const requestId = this.orderStatisticsRequestId + 1
    this.orderStatisticsRequestId = requestId
    this.orderStatisticsLoading = true
    this.orderStatisticsError = false
    this.orderStatistics = null

    try {
      const { data } = await MainService.getOrderStatistics(storeId, days)
      runInAction(() => {
        if (requestId === this.orderStatisticsRequestId) {
          this.orderStatistics = data
        }
      })
    } catch {
      runInAction(() => {
        if (requestId === this.orderStatisticsRequestId) {
          this.orderStatisticsError = true
        }
      })
    } finally {
      runInAction(() => {
        if (requestId === this.orderStatisticsRequestId) {
          this.orderStatisticsLoading = false
        }
      })
    }
  }

  getRestock = async (storeId: string) => {
    const requestId = this.restockRequestId + 1
    this.restockRequestId = requestId
    this.restockLoading = true
    this.restockError = false
    this.restockProducts = []

    try {
      const { data } = await MainService.getRestock(storeId)
      runInAction(() => {
        if (requestId === this.restockRequestId) {
          this.restockProducts = data
        }
      })
    } catch {
      runInAction(() => {
        if (requestId === this.restockRequestId) {
          this.restockError = true
        }
      })
    } finally {
      runInAction(() => {
        if (requestId === this.restockRequestId) {
          this.restockLoading = false
        }
      })
    }
  }

  getStoresMain = async (store: Stores) => {
    this.filterStore = {
      label: store.name,
      value: store.id,
    }
    await Promise.all([
      this.getStatistics(store.id),
      this.getRestock(store.id),
      this.getOrderStatistics(store.id, this.statisticsDays),
    ])
  }

  setFilterStore = (value: SelectOption) => {
    this.filterStore = value
    const storeId = value.value
    this.getStatistics(storeId)
    this.getRestock(storeId)
    this.getOrderStatistics(storeId, this.statisticsDays)
  }

  setStatisticsDays = (days: number) => {
    if (!Number.isInteger(days) || days < 1 || days > 366) {
      return
    }

    this.statisticsDays = days
    if (this.filterStore) {
      this.getStatistics(this.filterStore.value, days)
      this.getOrderStatistics(this.filterStore.value, days)
    }
  }

  retryStatistics = () => {
    if (this.filterStore) {
      return this.getStatistics(this.filterStore.value)
    }
    return Promise.resolve()
  }

  retryOrderStatistics = () => {
    if (this.filterStore) {
      return this.getOrderStatistics(
        this.filterStore.value,
        this.statisticsDays,
      )
    }
    return Promise.resolve()
  }

  retryRestock = () => {
    if (this.filterStore) {
      return this.getRestock(this.filterStore.value)
    }
    return Promise.resolve()
  }
}

export default MainModel
